/** HEIC via Web Worker. Lazy init, 30s idle timeout; fallback in helpers/images.ts. */
import { logger } from '@/lib/logger'

const WORKER_OP_TIMEOUT_MS = 60_000
const WORKER_IDLE_TIMEOUT_MS = 30_000

let workerInstance: Worker | null = null
let idleTimer: ReturnType<typeof setTimeout> | null = null
/** When true, worker is skipped for rest of session; callers use main-thread fallback. */
let workerFailed = false

function clearIdleTimer() {
  if (idleTimer !== null) {
    clearTimeout(idleTimer)
    idleTimer = null
  }
}

function resetIdleTimer() {
  clearIdleTimer()
  idleTimer = setTimeout(() => {
    terminateHeicWorker()
  }, WORKER_IDLE_TIMEOUT_MS)
}

function getOrCreateWorker(): Worker | null {
  if (workerFailed) return null

  if (workerInstance) return workerInstance

  try {
    workerInstance = new Worker(
      new URL('../workers/heicConvert.worker.ts', import.meta.url)
    )

    workerInstance.onerror = (event) => {
      logger.error('[heicWorker] Worker error — switching to fallback', event)
      workerFailed = true
      workerInstance = null
      clearIdleTimer()
    }

    return workerInstance
  } catch (err) {
    logger.error(
      '[heicWorker] Failed to create Worker — will use fallback',
      err
    )
    workerFailed = true
    return null
  }
}

/** Call when upload UI unmounts to release the worker. */
export function terminateHeicWorker(): void {
  clearIdleTimer()
  if (workerInstance) {
    workerInstance.terminate()
    workerInstance = null
  }
}

/** Call when upload UI opens to warm worker so first HEIC does not wait for init. */
export function preloadHeicWorker(): void {
  getOrCreateWorker()
}

/** Converts HEIC/HEIF to JPEG in worker. Throws if worker unavailable (caller uses fallback). */
export async function convertHeicViaWorker(file: File): Promise<File> {
  const w = getOrCreateWorker()
  if (!w) throw new Error('HEIC worker unavailable')
  const workerRef: Worker = w

  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
  // Yield before file read so UI can paint.
  await new Promise<void>((r) => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(() => r(), { timeout: 120 })
    } else {
      setTimeout(r, 0)
    }
  })
  const inputBuffer = await file.arrayBuffer()

  const resultBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup()
      reject(new Error('HEIC worker conversion timed out'))
    }, WORKER_OP_TIMEOUT_MS)

    const onMessage = (event: MessageEvent) => {
      const data = event.data as {
        id: string
        buffer?: ArrayBuffer
        error?: string
      }
      if (data.id !== id) return
      cleanup()
      if (data.error) {
        reject(new Error(data.error))
      } else if (data.buffer instanceof ArrayBuffer) {
        resolve(data.buffer)
      } else {
        reject(new Error('HEIC worker returned unexpected response'))
      }
    }

    const onError = (event: ErrorEvent) => {
      cleanup()
      reject(new Error(`HEIC worker error: ${event.message}`))
    }

    function cleanup() {
      clearTimeout(timeout)
      workerRef.removeEventListener('message', onMessage)
      workerRef.removeEventListener('error', onError)
    }

    workerRef.addEventListener('message', onMessage)
    workerRef.addEventListener('error', onError)

    workerRef.postMessage({ id, buffer: inputBuffer }, [inputBuffer])
  })

  resetIdleTimer()

  const fileName = file.name.replace(/\.(heic|heif)$/i, '.jpg')
  const blob = new Blob([resultBuffer], { type: 'image/jpeg' })
  return new File([blob], fileName, { type: 'image/jpeg' })
}
